/* Video highlight (OL-3536): the two custom controls (play/pause, mute)
   and intersection-driven autoplay/pause.

   There is no native <video controls> bar here at all - by design, per
   the brief, the only controls are these two buttons - so this file IS
   the control surface, not a decorative layer on top of one. If it fails
   to load, the video simply cannot be started (poster image only), which
   is the honest tradeoff of removing native controls entirely: unlike
   round 1's decorative badge over native controls, there is no fallback
   control path left once this script is gone. */
(function () {
  // Icon markup as constants, swapped wholesale into a single slot per
  // button - the same mechanism the existing homepage practitioner video
  // component uses (ICON_PLAY/ICON_PAUSE/ICON_MUTED/ICON_SOUND there),
  // adopted here deliberately rather than the two-sibling-SVG-plus-
  // hidden-attribute approach this file used before: that depended on
  // the browser's native [hidden] behaviour not being overridden by
  // other CSS, which is exactly what broke it once already (see OL-3536
  // PR #96). A slot that only ever contains the current icon's markup
  // has nothing left to override.
  var ICON_PLAY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  var ICON_PAUSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 19h4V5H6zm8-14v14h4V5z"/></svg>';
  var ICON_MUTED = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>';
  var ICON_UNMUTED = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>';

  function setPlayingState(media, video) {
    var playing = !video.paused && !video.ended;
    media.classList.toggle('is-playing', playing);

    var toggle = media.querySelector('[data-video-highlight-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    toggle.setAttribute('aria-pressed', String(playing));

    var icon = toggle.querySelector('[data-icon]');
    if (icon) icon.innerHTML = playing ? ICON_PAUSE : ICON_PLAY;
  }

  function setMuteState(media, video) {
    var muteButton = media.querySelector('[data-video-highlight-mute]');
    if (!muteButton) return;
    muteButton.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    muteButton.setAttribute('aria-pressed', String(video.muted));

    var icon = muteButton.querySelector('[data-icon]');
    if (icon) icon.innerHTML = video.muted ? ICON_MUTED : ICON_UNMUTED;
  }

  function bind(media) {
    if (media.dataset.videoHighlightBound) return;
    media.dataset.videoHighlightBound = 'true';

    var video = media.querySelector('[data-video-highlight]');
    var toggle = media.querySelector('[data-video-highlight-toggle]');
    var muteButton = media.querySelector('[data-video-highlight-mute]');
    if (!video) return;

    var togglePlayback = function () {
      if (video.paused || video.ended) {
        video.play().catch(function () {
          // Rejected (e.g. no user gesture on some platforms) - resync
          // to the video's actual state, which is still paused.
          setPlayingState(media, video);
        });
      } else {
        video.pause();
      }
      // Read synchronously right after calling play()/pause(): per spec
      // `paused` flips as one of the first steps of either call, before
      // the browser fires the matching event, not after - so this is
      // already correct, not merely optimistic. Updating here rather
      // than waiting on the 'play'/'pause' event round-trip means the
      // icon can't be left stuck if something about the embedding
      // context (the theme editor's own section-preview iframe, for
      // instance) delays or drops that event. The listeners below stay
      // in place for changes that don't originate from this button, e.g.
      // the IntersectionObserver-driven autoplay/pause on scroll.
      setPlayingState(media, video);
    };

    if (toggle) {
      toggle.addEventListener('click', togglePlayback);
    }
    // Clicking the video itself also toggles playback - a larger, more
    // natural target than the button alone, matching how short-form
    // video is usually expected to behave.
    video.addEventListener('click', togglePlayback);

    if (muteButton) {
      muteButton.addEventListener('click', function (event) {
        // Stops the click reaching the video underneath, which would
        // otherwise also toggle playback via the listener above.
        event.stopPropagation();
        video.muted = !video.muted;
        setMuteState(media, video);
      });
    }

    video.addEventListener('play', function () {
      setPlayingState(media, video);
    });
    video.addEventListener('pause', function () {
      setPlayingState(media, video);
    });
    video.addEventListener('ended', function () {
      setPlayingState(media, video);
    });
    video.addEventListener('volumechange', function () {
      setMuteState(media, video);
    });

    setPlayingState(media, video);
    setMuteState(media, video);

    // Autoplay: intersection-driven rather than the native `autoplay`
    // attribute, so a below-the-fold video does not start playing (and
    // downloading) before the shopper has scrolled anywhere near it, and
    // pauses again once it scrolls back out of view.
    if (media.dataset.autoplay === 'true' && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              video.play().catch(function () {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      );
      observer.observe(media);
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-video-highlight-media]').forEach(bind);
  }

  document.addEventListener('DOMContentLoaded', function () {
    init();
  });

  // Theme editor: sections are re-rendered on edit, so re-bind the new nodes.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
